import { Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react'
import { useState } from 'react'
import type { FormItemConfig, ResultPropertyConfig, TemplateConfig } from '@/types/template-config'

interface FieldConfigEditorProps {
  config: TemplateConfig
  onChange: (config: TemplateConfig) => void
}

export default function FieldConfigEditor({ config, onChange }: FieldConfigEditorProps) {
  const [showFormItems, setShowFormItems] = useState(true)
  const [showResultProps, setShowResultProps] = useState(true)

  const addFormItem = () => {
    const newItem: FormItemConfig = {
      key: `field_${Date.now()}`,
      labelZhCN: '新字段',
      labelEnUS: 'New Field',
      labelJaJP: '新しいフィールド',
      fieldType: 'Text',
      component: 'FieldSelect',
      supportType: ['Text'],
      required: false,
    }
    onChange({
      ...config,
      formItems: [...config.formItems, newItem]
    })
  }

  const removeFormItem = (index: number) => {
    onChange({
      ...config,
      formItems: config.formItems.filter((_, i) => i !== index)
    })
  }

  const updateFormItem = (index: number, updates: Partial<FormItemConfig>) => {
    const newFormItems = [...config.formItems]
    newFormItems[index] = { ...newFormItems[index], ...updates }
    onChange({ ...config, formItems: newFormItems })
  }

  const addResultProperty = () => {
    const newProp: ResultPropertyConfig = {
      key: `result_${Date.now()}`,
      labelZhCN: '新结果',
      labelEnUS: 'New Result',
      labelJaJP: '新しい結果',
      fieldType: 'Text',
      primary: false,
      hidden: false,
    }
    onChange({
      ...config,
      resultProperties: [...config.resultProperties, newProp]
    })
  }

  const removeResultProperty = (index: number) => {
    onChange({
      ...config,
      resultProperties: config.resultProperties.filter((_, i) => i !== index)
    })
  }

  const updateResultProperty = (index: number, updates: Partial<ResultPropertyConfig>) => {
    const newResultProps = [...config.resultProperties]
    newResultProps[index] = { ...newResultProps[index], ...updates }
    onChange({ ...config, resultProperties: newResultProps })
  }

  return (
    <div className="space-y-4 text-sm">
      {/* 输入字段配置 */}
      <div className="bg-white rounded-lg border border-gray-200">
        <button
          onClick={() => setShowFormItems(!showFormItems)}
          className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition"
        >
          <span className="font-semibold text-gray-900">输入字段 (formItems)</span>
          {showFormItems ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {showFormItems && (
          <div className="p-3 border-t border-gray-200 space-y-3">
            {config.formItems.map((item, index) => (
              <div key={index} className="p-3 bg-gray-50 rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-mono text-gray-600">{item.key}</span>
                  <button
                    onClick={() => removeFormItem(index)}
                    className="text-red-600 hover:text-red-700 p-1"
                    title="删除"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <input
                  type="text"
                  value={item.key}
                  onChange={(e) => updateFormItem(index, { key: e.target.value })}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                  placeholder="字段 key"
                />

                <input
                  type="text"
                  value={item.labelZhCN}
                  onChange={(e) => updateFormItem(index, { labelZhCN: e.target.value })}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                  placeholder="中文标签"
                />

                <input
                  type="text"
                  value={item.labelEnUS}
                  onChange={(e) => updateFormItem(index, { labelEnUS: e.target.value })}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                  placeholder="英文标签"
                />

                <select
                  value={item.fieldType}
                  onChange={(e) => updateFormItem(index, {
                    fieldType: e.target.value as any,
                    supportType: [e.target.value]
                  })}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                >
                  <option value="Text">文本 (Text)</option>
                  <option value="Number">数字 (Number)</option>
                  <option value="Attachment">附件 (Attachment)</option>
                </select>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={item.required || false}
                    onChange={(e) => updateFormItem(index, { required: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-xs text-gray-700">必填</span>
                </label>
              </div>
            ))}

            <button
              onClick={addFormItem}
              className="w-full flex items-center justify-center gap-2 p-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition text-gray-600 hover:text-blue-600"
            >
              <Plus className="w-4 h-4" />
              <span className="text-xs font-medium">添加输入字段</span>
            </button>
          </div>
        )}
      </div>

      {/* 输出字段配置 */}
      <div className="bg-white rounded-lg border border-gray-200">
        <button
          onClick={() => setShowResultProps(!showResultProps)}
          className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition"
        >
          <span className="font-semibold text-gray-900">输出字段 (resultType)</span>
          {showResultProps ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {showResultProps && (
          <div className="p-3 border-t border-gray-200 space-y-3">
            {config.resultProperties.map((prop, index) => (
              <div key={index} className="p-3 bg-gray-50 rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-mono text-gray-600">{prop.key}</span>
                  {prop.key !== 'id' && (
                    <button
                      onClick={() => removeResultProperty(index)}
                      className="text-red-600 hover:text-red-700 p-1"
                      title="删除"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <input
                  type="text"
                  value={prop.key}
                  onChange={(e) => updateResultProperty(index, { key: e.target.value })}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                  placeholder="字段 key"
                  disabled={prop.key === 'id'}
                />

                <input
                  type="text"
                  value={prop.labelZhCN}
                  onChange={(e) => updateResultProperty(index, { labelZhCN: e.target.value })}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                  placeholder="中文标签"
                />

                <input
                  type="text"
                  value={prop.labelEnUS}
                  onChange={(e) => updateResultProperty(index, { labelEnUS: e.target.value })}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                  placeholder="英文标签"
                />

                <select
                  value={prop.fieldType}
                  onChange={(e) => updateResultProperty(index, { fieldType: e.target.value as any })}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                >
                  <option value="Text">文本 (Text)</option>
                  <option value="Number">数字 (Number)</option>
                  <option value="Object">对象 (Object)</option>
                </select>

                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={prop.primary || false}
                      onChange={(e) => updateResultProperty(index, { primary: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-xs text-gray-700">主字段</span>
                  </label>

                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={prop.hidden || false}
                      onChange={(e) => updateResultProperty(index, { hidden: e.target.checked })}
                      className="rounded"
                      disabled={prop.key === 'id'}
                    />
                    <span className="text-xs text-gray-700">隐藏</span>
                  </label>
                </div>
              </div>
            ))}

            <button
              onClick={addResultProperty}
              className="w-full flex items-center justify-center gap-2 p-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition text-gray-600 hover:text-blue-600"
            >
              <Plus className="w-4 h-4" />
              <span className="text-xs font-medium">添加输出字段</span>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
